import { message } from 'antd';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as agentComputerApi from '../services/agentComputerApi';
import UserStreamManager from '../utils/UserStreamManager';
import { useCurrentChat } from './CurrentChatContext';
import { useMediaViewer } from './MediaViewerContext';

const AgentComputerContext = createContext();

export const useAgentComputer = () => {
  const context = useContext(AgentComputerContext);
  if (!context) {
    throw new Error('useAgentComputer must be used within AgentComputerProvider');
  }
  return context;
};

export const AgentComputerProvider = ({ children }) => {
  const { currentChat } = useCurrentChat();
  const { openMedia } = useMediaViewer();

  const [tabId] = useState(() => uuidv4());

  const [state, setState] = useState({
    isOpen: false,
    sessionId: null,
    currentPath: '/home',
    items: [],
    loading: false,
    containerStarting: false,  // NEW: Track container startup separately
    containerRunning: true,
    agentActive: false,
    error: null,
  });

  const chatId = currentChat?.id;
  const heartbeatIntervalRef = useRef(null);
  const currentPathRef = useRef(state.currentPath);

  useEffect(() => {
    currentPathRef.current = state.currentPath;
  }, [state.currentPath]);

  useEffect(() => {
    if (state.isOpen && state.sessionId) {
      closeAgentComputer();
    }
  }, [chatId]);

  useEffect(() => {
    if (!state.isOpen || !state.sessionId || !chatId) {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      return;
    }

    const sendHeartbeat = async () => {
      try {
        const { data } = await agentComputerApi.heartbeat(
          chatId,
          state.sessionId,
          currentPathRef.current
        );

        if (data.container_running !== state.containerRunning) {
          setState(prev => ({
            ...prev,
            containerRunning: data.container_running
          }));

          if (!data.container_running) {
            setState(prev => ({
              ...prev,
              error: {
                type: 'container_stopped',
                message: 'Container stopped due to inactivity'
              }
            }));
          }
        }
      } catch (error) {
        console.error('Heartbeat failed:', error);

        if (error.response?.status === 404) {
          setState(prev => ({
            ...prev,
            error: {
              type: 'session_expired',
              message: 'Your session has expired'
            }
          }));
        }
      }
    };

    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 30000);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [state.isOpen, state.sessionId, chatId, state.containerRunning]);

  useEffect(() => {
    return () => {
      if (state.sessionId && chatId) {
        agentComputerApi.endSession(chatId, state.sessionId).catch(err => {
          console.error('Failed to end session on unmount:', err);
        });
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (state.sessionId && chatId) {
        const url = `${agentComputerApi.default.defaults.baseURL}/${chatId}/session/${state.sessionId}`;
        navigator.sendBeacon(url, new Blob([], { type: 'application/json' }));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.sessionId, chatId]);

  useEffect(() => {
    if (!chatId || !state.isOpen) return;

    const handleRefresh = async (data) => {
      if (data.chat_id === chatId) {
        try {
          const { data: dirData } = await agentComputerApi.listDirectory(
            chatId,
            currentPathRef.current
          );

          setState(prev => ({
            ...prev,
            items: dirData.items
          }));
        } catch (error) {
          console.error('Failed to refresh directory:', error);
        }
      }
    };

    const handleContainerStatus = (data) => {
      if (data.chat_id === chatId) {
        setState(prev => ({
          ...prev,
          containerRunning: data.container_running
        }));

        if (!data.container_running) {
          setState(prev => ({
            ...prev,
            error: {
              type: 'container_stopped',
              message: 'Container stopped'
            }
          }));
        }
      }
    };

    const unsubscribeRefresh = UserStreamManager.subscribe(
      'AGENT_COMPUTER_REFRESH',
      handleRefresh
    );

    const unsubscribeStatus = UserStreamManager.subscribe(
      'AGENT_COMPUTER_CONTAINER_STATUS',
      handleContainerStatus
    );

    return () => {
      unsubscribeRefresh();
      unsubscribeStatus();
    };
  }, [chatId, state.isOpen]);

  const openAgentComputer = useCallback(async () => {
    if (!chatId) {
      message.error('No active chat');
      return;
    }

    setState(prev => ({
      ...prev,
      loading: true,
      containerStarting: true,  // NEW: Show starting state
      error: null
    }));

    try {
      const { data: session } = await agentComputerApi.createSession(
        chatId,
        tabId,
        true
      );

      const { data: dirList } = await agentComputerApi.listDirectory(
        chatId,
        '/home'
      );

      setState(prev => ({
        ...prev,
        isOpen: true,
        sessionId: session.session_id,
        containerRunning: session.container_running,
        currentPath: '/home',
        items: dirList.items,
        loading: false,
        containerStarting: false,  // NEW: Clear starting state
        error: null
      }));

      // REMOVED: message.success('Agent Computer opened') - redundant visual feedback

    } catch (error) {
      console.error('Failed to open Agent Computer:', error);

      let errorMessage = 'Failed to open Agent Computer';
      let errorType = 'open_failed';

      if (error.response?.status === 503) {
        errorMessage = 'Container failed to start. Please try again.';
        errorType = 'container_start_failed';
      } else if (error.response?.status === 404) {
        errorMessage = 'Chat not found';
        errorType = 'chat_not_found';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      setState(prev => ({
        ...prev,
        loading: false,
        containerStarting: false,
        error: { type: errorType, message: errorMessage }
      }));

      message.error(errorMessage);
    }
  }, [chatId, tabId]);

  const closeAgentComputer = useCallback(async () => {
    if (state.sessionId && chatId) {
      try {
        await agentComputerApi.endSession(chatId, state.sessionId);
      } catch (error) {
        console.error('Failed to end session:', error);
      }
    }

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    setState({
      isOpen: false,
      sessionId: null,
      currentPath: '/home',
      items: [],
      loading: false,
      containerStarting: false,
      containerRunning: true,
      agentActive: false,
      error: null
    });
  }, [state.sessionId, chatId]);

  const navigateToFolder = useCallback(async (path) => {
    if (!chatId || !state.sessionId) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { data } = await agentComputerApi.listDirectory(chatId, path);

      setState(prev => ({
        ...prev,
        currentPath: path,
        items: data.items,
        loading: false
      }));

    } catch (error) {
      console.error('Failed to navigate:', error);

      let errorMessage = 'Failed to open folder';
      if (error.response?.status === 404) {
        errorMessage = 'Directory not found';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: { type: 'navigation_failed', message: errorMessage }
      }));

      message.error(errorMessage);
    }
  }, [chatId, state.sessionId]);

  const refreshCurrentDirectory = useCallback(async () => {
    if (!chatId || !state.isOpen || !state.sessionId) return;

    try {
      const { data } = await agentComputerApi.listDirectory(
        chatId,
        currentPathRef.current
      );

      setState(prev => ({
        ...prev,
        items: data.items
      }));

    } catch (error) {
      console.error('Failed to refresh directory:', error);
    }
  }, [chatId, state.isOpen, state.sessionId]);

  const handleFileClick = useCallback(async (file) => {
    if (file.name.endsWith('.ccapp')) {
      try {
        const response = await fetch(
          agentComputerApi.getFileUrl(chatId, file.path),
          { credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch .ccapp: ${response.status}`);
        }

        const ccappData = await response.json();

        if (ccappData.app_url) {
          const newWindow = window.open(ccappData.app_url, '_blank', 'noopener,noreferrer');
          if (!newWindow) {
            message.warning('Pop-up blocked. Please allow pop-ups to open apps.');
          }
        } else {
          message.error('Invalid .ccapp file - missing app_url');
        }
      } catch (error) {
        console.error('Failed to open .ccapp:', error);
        message.error('Failed to open app');
      }
      return;
    }

    const viewableFiles = state.items.filter(item =>
      item.type === 'file' && isViewableType(item.mime_type)
    );

    const mediaFiles = viewableFiles.map(item => ({
      url: agentComputerApi.getFileUrl(chatId, item.path),
      original_filename: item.name,
      mime_type: item.mime_type,
      file_size: item.size,
      file_type: getMediaType(item.mime_type)
    }));

    const initialIndex = mediaFiles.findIndex(
      m => m.original_filename === file.name
    );

    openMedia(mediaFiles, initialIndex >= 0 ? initialIndex : 0);

  }, [state.items, chatId, openMedia]);

  const handleRestartContainer = useCallback(async () => {
    if (!chatId) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { data: session } = await agentComputerApi.createSession(
        chatId,
        tabId,
        true
      );

      const { data: dirList } = await agentComputerApi.listDirectory(
        chatId,
        state.currentPath
      );

      setState(prev => ({
        ...prev,
        sessionId: session.session_id,
        containerRunning: session.container_running,
        items: dirList.items,
        loading: false,
        error: null
      }));

      message.success('Container restarted');

    } catch (error) {
      console.error('Failed to restart container:', error);

      setState(prev => ({
        ...prev,
        loading: false,
        error: {
          type: 'restart_failed',
          message: error.response?.data?.detail || 'Failed to restart container'
        }
      }));

      message.error('Failed to restart container');
    }
  }, [chatId, tabId, state.currentPath]);

  const value = {
    ...state,
    openAgentComputer,
    closeAgentComputer,
    navigateToFolder,
    refreshCurrentDirectory,
    handleFileClick,
    handleRestartContainer
  };

  return (
    <AgentComputerContext.Provider value={value}>
      {children}
    </AgentComputerContext.Provider>
  );
};

function isViewableType(mimeType) {
  if (!mimeType) return false;

  return (
    mimeType.startsWith('image/') ||
    mimeType.startsWith('video/') ||
    mimeType.startsWith('audio/') ||
    mimeType === 'application/pdf' ||
    mimeType.startsWith('text/') ||
    mimeType === 'application/json' ||
    mimeType === 'application/javascript'
  );
}

function getMediaType(mimeType) {
  if (!mimeType) return 'document';

  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'document';
  return 'document';
}
